// c06 / c03 Corp Anniversary — corporate paper-invitation preview.
// Outer steel-foil frame using template accent + bg. Eyebrow "TADBIR" →
// Roman-numeral diamond medallion (or sepia company photo) → names in
// template font → years span → date + venue caption.

import { Photo, type LayoutCtx } from "./_shared";

export function CorpAnniversaryLayout({
  names, date, venue, accent, ink, bg, font, photo,
}: LayoutCtx) {
  const captionDate = formatDate(date);
  const yearsMatch = names.match(/(\d{1,3})\s*YEARS?/i) || names.match(/(\d{1,3})/);
  const yearsNum = yearsMatch ? parseInt(yearsMatch[1], 10) : 10;
  const roman = toRoman(yearsNum);
  const dateYearMatch = date.match(/(\d{4})$/);
  const endYear = dateYearMatch ? parseInt(dateYearMatch[1], 10) : 2026;
  const startYear = endYear - yearsNum;

  const FOIL = `linear-gradient(135deg, ${accent} 0%, ${bg} 22%, ${accent}aa 45%, ${bg} 65%, ${accent} 100%)`;

  return (
    <div style={{ position: "absolute", inset: 0, background: FOIL, padding: "5%", display: "flex" }}>
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          position: "relative",
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55, inset 0 0 0 4px ${bg}, inset 0 0 0 5px ${accent}33`,
        }}
      >
        <div style={{ fontFamily: "var(--font-ui)", fontSize: 7, fontWeight: 700, letterSpacing: "0.4em", color: accent, marginBottom: "4%" }}>
          TADBIR · EST. {startYear}
        </div>

        {/* Roman-numeral diamond medallion */}
        <div style={{ width: "60%", aspectRatio: "1/1", position: "relative", marginBottom: "5%", display: "grid", placeItems: "center" }}>
          <svg viewBox="0 0 100 100" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} aria-hidden>
            <g fill="none" stroke={accent} strokeWidth="0.8">
              <rect x="14" y="14" width="72" height="72" transform="rotate(45 50 50)" />
              <rect x="22" y="22" width="56" height="56" transform="rotate(45 50 50)" strokeWidth="0.4" opacity="0.6" />
            </g>
            <circle cx="50" cy="50" r="22" fill="none" stroke={accent} strokeWidth="0.6" opacity="0.8" />
          </svg>
          <div
            style={{
              width: "44%",
              height: "44%",
              borderRadius: "50%",
              overflow: "hidden",
              border: `1px solid ${accent}88`,
              position: "relative",
              background: bg,
            }}
          >
            {photo ? (
              <Photo src={photo} sepia style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} />
            ) : (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "grid",
                  placeItems: "center",
                  background: `radial-gradient(circle at 50% 50%, ${accent}22 0%, ${bg} 75%)`,
                  fontFamily: font,
                  fontSize: 26,
                  color: accent,
                  letterSpacing: "0.06em",
                }}
              >
                {roman}
              </div>
            )}
          </div>
        </div>

        <div
          style={{
            fontFamily: font,
            fontSize: 20,
            lineHeight: 1.05,
            color: ink,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
          }}
        >
          {names}
        </div>

        <div style={{ fontFamily: font, fontSize: 11, color: `${ink}cc`, letterSpacing: "0.18em", marginTop: 6 }}>
          {startYear} — {endYear}
        </div>

        <div style={{ flex: 1 }} />

        <div style={{ fontFamily: "var(--font-ui)", fontSize: 6.5, fontWeight: 700, letterSpacing: "0.32em", color: accent, marginTop: "4%" }}>
          {roman} · {captionDate}
        </div>
        <div style={{ fontFamily: "var(--font-ui)", fontSize: 6, fontWeight: 600, letterSpacing: "0.28em", color: `${ink}99`, marginTop: 6 }}>
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}

function formatDate(d: string): string {
  const m = d.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (!m) return d.toUpperCase();
  const months = [
    "YANVAR", "FEVRAL", "MART", "APREL", "MAY", "IYUN",
    "IYUL", "AVGUST", "SENTABR", "OKTABR", "NOYABR", "DEKABR",
  ];
  return `${parseInt(m[1], 10)} ${months[parseInt(m[2], 10) - 1] ?? ""} ${m[3]}`;
}

function toRoman(num: number): string {
  if (num <= 0 || num >= 4000) return String(num);
  const map: Array<[number, string]> = [
    [1000, "M"], [900, "CM"], [500, "D"], [400, "CD"],
    [100, "C"], [90, "XC"], [50, "L"], [40, "XL"],
    [10, "X"], [9, "IX"], [5, "V"], [4, "IV"], [1, "I"],
  ];
  let n = num;
  let out = "";
  for (const [v, s] of map) { while (n >= v) { out += s; n -= v; } }
  return out;
}
